import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, PackageX, ShoppingCart, UserPlus, Wallet } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../../lib/api";
import { fmtTRY, fmtDate, ORDER_STATUS_COLORS } from "../../lib/format";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 bg-[#181B20] rounded-2xl animate-pulse-soft" />)}</div>;

  const kpis = [
    { label: "Toplam Satış", value: fmtTRY(data.total_sales), icon: Wallet, color: "text-emerald-400", id: "admin-dashboard-kpi-sales" },
    { label: "Bugünkü Satış", value: fmtTRY(data.today_sales), icon: Wallet, color: "text-emerald-400" },
    { label: "Toplam Sipariş", value: data.total_orders, icon: ShoppingCart, color: "text-blue-400" },
    { label: "Bugünkü Sipariş", value: data.today_orders, icon: ShoppingCart, color: "text-blue-400" },
    { label: "Bekleyen Sipariş", value: data.pending_orders, icon: AlertTriangle, color: "text-amber-400" },
    { label: "Yeni Müşteri (7g)", value: data.new_customers, icon: UserPlus, color: "text-purple-400" },
    { label: "Kritik Stok", value: data.critical_products.length, icon: AlertTriangle, color: "text-rose-400" },
    { label: "Stokta Olmayan", value: data.out_of_stock, icon: PackageX, color: "text-rose-400" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="font-display font-extrabold text-2xl text-white">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5" data-testid={k.id || `kpi-${k.label}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-neutral-500">{k.label}</p>
              <k.icon size={16} className={k.color} />
            </div>
            <p className="font-mono-ed font-bold text-xl sm:text-2xl text-white">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
        <h2 className="font-semibold text-white mb-4 text-sm">Kanal Bazlı Satış</h2>
        <div className="grid grid-cols-3 gap-3" data-testid="channel-sales">
          {[["BIGELDI", "Bigeldi.com"], ["TRENDYOL", "Trendyol"], ["HEPSIBURADA", "Hepsiburada"]].map(([ch, label]) => (
            <div key={ch} className="bg-[#0F1115] rounded-xl p-4 text-center" data-testid={`channel-sales-${ch.toLowerCase()}`}>
              <p className="text-[11px] text-neutral-500 mb-1">{label}</p>
              <p className="font-mono-ed font-bold text-lg text-white">{fmtTRY(data.channel_sales?.[ch]?.total || 0)}</p>
              <p className="text-[10px] text-neutral-600">{data.channel_sales?.[ch]?.count || 0} sipariş</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
        <h2 className="font-semibold text-white mb-4 text-sm">Son 14 Gün Satış Grafiği</h2>
        <div className="h-56" data-testid="sales-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.chart}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#272B33" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} width={70}
                tickFormatter={(v) => `₺${v.toLocaleString("tr-TR")}`} />
              <Tooltip contentStyle={{ background: "#181B20", border: "1px solid #272B33", borderRadius: 12, fontSize: 12 }}
                formatter={(v, name) => [name === "sales" ? fmtTRY(v) : v, name === "sales" ? "Satış" : "Sipariş"]} />
              <Area type="monotone" dataKey="sales" stroke="#2563EB" strokeWidth={2.5} fill="url(#salesGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data.payment_stats && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
          <h2 className="font-semibold text-white mb-3 text-sm">Ödeme İstatistikleri</h2>
          <div className="flex flex-wrap gap-2" data-testid="payment-stats">
            {[["Başarılı", data.payment_stats.success, "text-emerald-400"], ["Başarısız", data.payment_stats.failed, "text-rose-400"],
              ["İptal", data.payment_stats.cancelled, "text-neutral-400"], ["İade", data.payment_stats.refunded, "text-amber-400"],
              ["Taksitli Satış", data.payment_stats.installment_sales, "text-blue-400"], ["Tek Çekim", data.payment_stats.single_sales, "text-blue-400"]].map(([label, val, color]) => (
              <span key={label} className="bg-[#0F1115] rounded-full px-4 py-2 text-xs">
                <span className="text-neutral-500">{label}:</span> <span className={`font-bold ${color}`}>{val}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {data.invoice_stats && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
          <h2 className="font-semibold text-white mb-3 text-sm">Fatura İstatistikleri</h2>
          <div className="flex flex-wrap gap-2" data-testid="invoice-stats">
            {[["Bugünkü", data.invoice_stats.today, "text-blue-400"], ["Bu Ay", data.invoice_stats.this_month, "text-blue-400"],
              ["e-Fatura", data.invoice_stats.e_invoice, "text-purple-400"], ["e-Arşiv", data.invoice_stats.e_archive, "text-purple-400"],
              ["Başarısız", data.invoice_stats.failed, "text-rose-400"], ["Bekleyen", data.invoice_stats.pending, "text-amber-400"],
              ["İptal", data.invoice_stats.cancelled, "text-neutral-400"]].map(([label, val, color]) => (
              <span key={label} className="bg-[#0F1115] rounded-full px-4 py-2 text-xs">
                <span className="text-neutral-500">{label}:</span> <span className={`font-bold ${color}`}>{val}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-white text-sm">Son Siparişler</h2>
            <Link to="/admin/siparisler" className="text-xs text-blue-400 hover:underline" data-testid="dashboard-all-orders">Tümü</Link>
          </div>
          <div className="space-y-2">
            {data.recent_orders.length === 0 && <p className="text-xs text-neutral-500 py-6 text-center">Henüz sipariş yok</p>}
            {data.recent_orders.map((o) => (
              <div key={o.order_id} className="flex items-center justify-between bg-[#0F1115] rounded-xl px-4 py-3">
                <div>
                  <p className="font-mono-ed text-sm text-white">{o.order_number}</p>
                  <p className="text-[11px] text-neutral-500">{o.contact?.name} · {fmtDate(o.created_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${ORDER_STATUS_COLORS[o.status] || "bg-neutral-700 text-neutral-300"}`} data-testid="admin-order-status-badge">{o.status}</span>
                  <span className="font-semibold text-sm text-white">{fmtTRY(o.total)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-white text-sm">Kritik Stok Uyarıları</h2>
            <Link to="/admin/urunler" className="text-xs text-blue-400 hover:underline" data-testid="dashboard-all-products">Ürünler</Link>
          </div>
          <div className="space-y-2">
            {data.critical_products.length === 0 && <p className="text-xs text-neutral-500 py-6 text-center">Kritik stokta ürün yok</p>}
            {data.critical_products.map((p) => (
              <div key={p.product_id} className="flex items-center justify-between bg-[#0F1115] rounded-xl px-4 py-3" data-testid="critical-stock-row">
                <div>
                  <p className="text-sm text-white">{p.name}</p>
                  <p className="font-mono-ed text-[11px] text-neutral-500">{p.sku}</p>
                </div>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${p.stock <= 0 ? "bg-rose-500/15 text-rose-400" : "bg-amber-500/15 text-amber-400"}`}>
                  {p.stock <= 0 ? "TÜKENDİ" : `${p.stock} kaldı`}{p.variant_count ? ` · ${p.variant_count} varyant` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Geliştirme günlüğü — son değişikliklerin özeti */}
      <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5" data-testid="dashboard-changelog">
        <p className="text-white font-semibold mb-3">Son Geliştirmeler (24 Eylül 2026)</p>
        <ul className="text-xs text-neutral-400 space-y-1.5 leading-relaxed list-disc pl-4">
          <li><b className="text-neutral-200">Ödeme adres formu düzeltildi</b> — CheckoutPage.jsx: adres alanı artık sınırsız/çok satırlı yazılabiliyor (focus kaybı hatası giderildi); 81 il + 973 ilçe dropdown'ları (constants/turkey.js); kayıtlı adres düzenleme/silme/kaydetme</li>
          <li><b className="text-neutral-200">Dadya fiyatları</b> — Kadın/Erkek Parfüm: 50 ML = 480 ₺, 100 ML = 720 ₺ (350 ürün); "2. Ürüne %50" kampanyası doğrulandı, aynen çalışıyor</li>
          <li><b className="text-neutral-200">Kategori sol menüsü</b> — CatalogPage.jsx: tüm kategori sayfalarında ana kategorinin alt kategorileri dinamik gösteriliyor, aktif kategori vurgulu; marka filtresi ayrı "Filtreler" bölümünde</li>
          <li><b className="text-neutral-200">Ödeme sağlayıcıları</b> — Ödeme Yöntemleri sayfası: Garanti/iyzico/PayTR panelden yönetiliyor (şifreli saklama, maskeli gösterim, bağlantı testi, varsayılan seçimi)</li>
          <li><b className="text-neutral-200">Pazaryerleri</b> — Trendyol/Hepsiburada/PTTAVM/Amazon kartları + panelden API bilgisi girişi + bağlantı testi; Siparişlerde Kaynak kolonu; Entegrasyon Logları ekranı</li>
          <li><b className="text-neutral-200">Ürün kartları</b> — tüm sayfalarda standart kare görsel alanı (object-contain), uniform kart yüksekliği; sayfalama taşması giderildi</li>
        </ul>
      </div>
    </div>
  );
}
